import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useWorkspaceStore } from '@/store/workspace';
import { useAuthStore } from '@/store/auth';
import { useSubscriptionStore } from '@/store/subscription';
import { supabase } from '@/lib/supabase';
import { track } from '@/lib/posthog';
import { Loader2, AlertCircle } from 'lucide-react';

const MAX_WORKSPACES = 3;

// Helper to make a name possessive
const makePossessive = (name: string): string => {
  if (!name) return '';
  // If name ends in 's', just add apostrophe; otherwise add 's
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
};

// Get default workspace name from user's first name
const getDefaultWorkspaceName = (fullName: string | undefined): string => {
  if (!fullName) return '';
  const firstName = fullName.split(' ')[0];
  if (!firstName) return '';
  return `${makePossessive(firstName)} Workspace`;
};

export function WorkspaceSetup() {
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const { user, isSuperadmin } = useAuthStore();
  const defaultName = getDefaultWorkspaceName(user?.name);

  const [name, setName] = useState(defaultName);
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [atWorkspaceLimit, setAtWorkspaceLimit] = useState(false);
  const { createWorkspace, isLoading, fetchWorkspaces, setCurrentWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();

  // Check workspaces on mount
  useEffect(() => {
    const checkWorkspaces = async () => {
      try {
        await fetchWorkspaces();
        const currentWorkspaces = useWorkspaceStore.getState().workspaces;

        // During onboarding, skip if user already has workspaces
        if (isOnboarding && currentWorkspaces.length > 0) {
          setCurrentWorkspace(currentWorkspaces[0]);
          navigate('/projects/first-project', { replace: true });
          return;
        }

        // Check if user has reached workspace limit (superadmins bypass this)
        if (!isSuperadmin && currentWorkspaces.length >= MAX_WORKSPACES) {
          setAtWorkspaceLimit(true);
        }
      } catch (err) {
        console.error('Error checking workspaces:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkWorkspaces();
  }, [navigate, fetchWorkspaces, setCurrentWorkspace, isOnboarding, isSuperadmin]);

  // Generate a random subdomain
  const generateSubdomain = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `ws-${timestamp}-${randomStr}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const subdomain = generateSubdomain();
      await createWorkspace(name, subdomain);

      track('workspace_created', { workspace_name: name, is_onboarding: isOnboarding });

      // Soft Stripe checkout: try to redirect the brand-new admin into
      // Stripe Checkout to start their 7-day Pro trial. If this fails
      // (no price configured, network blip, etc.) or if the user later
      // cancels checkout, the workspace still exists at free tier and
      // they can upgrade from Settings whenever.
      const priceId = import.meta.env.VITE_STRIPE_PRO_PRICE_ID;
      if (priceId) {
        try {
          const checkoutUrl = await useSubscriptionStore
            .getState()
            .createCheckoutSession(priceId);
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
        } catch (checkoutErr) {
          console.error('Skipping checkout redirect:', checkoutErr);
        }
      }

      // Fallback / cancel path: drop the user into their workspace at
      // free tier. They can hit Upgrade from Settings later.
      navigate('/projects/first-project');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create workspace';
      setError(errorMessage);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Show loading while checking existing data
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600" />
      </div>
    );
  }

  // Show limit reached message
  if (atWorkspaceLimit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-amber-100 rounded-full w-fit">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Workspace Limit Reached
            </CardTitle>
            <CardDescription className="text-base">
              You've reached the maximum of {MAX_WORKSPACES} workspaces.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              To create a new workspace, please delete an existing one first. You can manage your workspaces from the workspace switcher in the dashboard.
            </p>

            <Button
              onClick={() => navigate('/home')}
              className="w-full"
            >
              Go to Dashboard
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-full"
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-ingesta-blue-600">
            Create Your Workspace
          </CardTitle>
          <CardDescription className="text-lg">
            Set up your team's project management hub
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Workspace Name
              </label>
              <Input
                id="name"
                type="text"
                placeholder="e.g., Acme Product Team"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                This will be displayed throughout your workspace
              </p>
            </div>

            {/* <div className="space-y-2">
              <label htmlFor="subdomain" className="text-sm font-medium">
                Subdomain
              </label>
              <div className="flex items-center">
                <Input 
                  id="subdomain"
                  type="text"
                  placeholder="acme"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                  className="flex-1"
                  required
                />
                <span className="ml-2 text-sm text-muted-foreground">
                  .ingesta.app
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your workspace will be accessible at {subdomain || 'your-subdomain'}.ingesta.app
              </p>
            </div> */}

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="bg-ingesta-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-ingesta-blue-800 mb-2">
                What's included in your workspace:
              </h3>
              <ul className="text-sm text-ingesta-blue-700 space-y-1">
                <li>• Unlimited projects and documents</li>
                <li>• Team collaboration and reader access</li>
                <li>• AI-powered project insights</li>
                {/* <li>• Weekly status report automation</li>
                <li>• Custom dashboard sections</li> */}
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? 'Creating Workspace...' : 'Create Workspace'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowLogoutDialog(true)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>
              You haven't created your workspace yet. You can complete this later by signing back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}